/**
 * NotificationPreferencesPage
 *
 * Allows customers to control which notifications they receive.
 * Toggle switches for email, SMS, and push notification preferences.
 * Auto-saves on change with toast feedback.
 *
 * Requirements: US-1 — Notification Preferences
 */

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Switch,
  LoadingSpinner,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { Bell, RefreshCw } from '@bloomcode/icons';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import type { NotificationPreferencesInput } from '@bloomcode/graphql';

/** Configuration for each preference toggle */
interface PreferenceConfig {
  key: keyof NotificationPreferencesInput;
  label: string;
  description: string;
}

const PREFERENCE_CONFIGS: PreferenceConfig[] = [
  {
    key: 'emailOnTicketResponse',
    label: 'Email on ticket response',
    description: 'Receive an email when your support ticket gets a response.',
  },
  {
    key: 'emailOnInvoice',
    label: 'Email on invoice',
    description: 'Receive an email when a new invoice is generated for your account.',
  },
  {
    key: 'emailOnAnnouncement',
    label: 'Email on announcement',
    description: 'Receive emails about product updates and announcements.',
  },
  {
    key: 'smsOnUrgent',
    label: 'SMS on urgent',
    description: 'Receive SMS messages for urgent notifications that need immediate attention.',
  },
  {
    key: 'pushNotifications',
    label: 'Push notifications',
    description: 'Receive push notifications in your browser for real-time updates.',
  },
];

/** A single preference toggle row */
function SwitchRow({
  config,
  checked,
  disabled,
  onToggle,
}: {
  config: PreferenceConfig;
  checked: boolean;
  disabled: boolean;
  onToggle: (key: keyof NotificationPreferencesInput, value: boolean) => void;
}) {
  const switchId = `pref-${config.key}`;

  return (
    <div className="flex items-center justify-between py-4">
      <div className="space-y-0.5 pr-4">
        <label htmlFor={switchId} className="text-sm font-medium cursor-pointer">
          {config.label}
        </label>
        <p className="text-sm text-muted-foreground">{config.description}</p>
      </div>
      <Switch
        id={switchId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value: boolean) => onToggle(config.key, value)}
        aria-label={config.label}
      />
    </div>
  );
}

export default function NotificationPreferencesPage() {
  const { preferences, isLoading, isUpdating, error, updatePreference, refetch } =
    useNotificationPreferences();

  if (isLoading && !preferences) {
    return (
      <>
        <Helmet>
          <title>Notification Preferences - Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner className="h-64" />
        </div>
      </>
    );
  }

  if (error && !preferences) {
    return (
      <>
        <Helmet>
          <title>Notification Preferences - Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <p className="text-destructive">Failed to load notification preferences.</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">
              Retry
            </Button>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Notification Preferences - Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Notification Preferences</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Bell className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold">Notification Preferences</h1>
              <p className="text-muted-foreground">
                Choose which notifications you want to receive.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Preferences Card */}
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="text-lg">Communication Channels</CardTitle>
            <CardDescription>
              Manage how and when you receive notifications from us.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y" role="list" aria-label="Notification preferences">
              {PREFERENCE_CONFIGS.map((config) => (
                <SwitchRow
                  key={config.key}
                  config={config}
                  checked={preferences?.[config.key] ?? false}
                  disabled={isUpdating}
                  onToggle={updatePreference}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
